import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { ReviewBody } from './ReviewBody'
import { ReviewComposer } from './ReviewComposer'
import { ShareSheet } from './ShareSheet'
import { ReelDisplay } from './ReelRating'
import { getValidSeasons } from '../lib/episode'
import { fmtDate } from '../lib/datetime'
import { Feather, Lock, Globe, Pencil, Share2, Plus, ArrowRight } from 'lucide-react'

// ── ReviewEvolution ───────────────────────────────────────────────────────────
// The "Your Take" card on a show — the AUTHORING surface (rate + write + revisit).
// Reading lives in the Reading Room, so this stays compact (Jason 2026-07-23): it shows
// only your LATEST take, and when you've reviewed more than once, a one-line arc
// (★★★★★ → ★★) that links to the full Then & Now timeline in the Reading Room. Writing,
// editing, and the "add an updated take" re-prompt happen here, in the show's context;
// your original is never overwritten (a revisit inserts a new row via forceNew).

function takeLabel(r) {
  if (r.milestone === 'finale') return 'Series finale'
  if (r.milestone === 'season' && r.season != null) return `After Season ${r.season}`
  if (r.season != null) return `Season ${r.season}`
  return 'Overall'
}

export function ReviewEvolution({ user, show, profile, rating = 0, onRate }) {
  const navigate = useNavigate()
  const [reviews, setReviews] = useState(null)   // null = loading; [] = none
  const [editTarget, setEditTarget] = useState(undefined) // undefined=closed
  const [shareRow, setShareRow] = useState(null)

  const load = useCallback(() => {
    if (!user?.id || !show?.id) { setReviews([]); return }
    supabase.from('reviews')
      .select('id, season, body, rating, milestone, is_public, contains_spoilers, created_at, updated_at')
      .eq('user_id', user.id).eq('show_id', show.id).is('episode', null)
      .order('created_at', { ascending: true })
      .then(({ data }) => setReviews((data || []).filter(r => (r.body || '').trim())))
  }, [user?.id, show?.id])
  useEffect(() => { setReviews(null); load() }, [load])

  if (!user || reviews === null) return null

  const seasons    = getValidSeasons(show?.seasons)
  const lastSeason = seasons.length ? Math.max(...seasons.map(s => s.season_number)) : null
  const showEnded  = /ended|cancel/i.test(show?.status || '')
  const closeEdit  = () => { setEditTarget(undefined); load() }

  const composer = editTarget !== undefined && (
    <ReviewComposer user={user} show={show} profile={profile} rating={rating} onRate={onRate}
      initialTarget={editTarget.season} isNew={editTarget.isNew} milestone={editTarget.milestone}
      forceNew={editTarget.forceNew} onClose={closeEdit} />
  )
  const shareSheet = shareRow && (
    <ShareSheet user={user} profile={profile}
      review={{ show_id: show.id, show_name: show.name, show_poster: show.poster_path, rating: shareRow.rating, body: shareRow.body, author: { display_name: profile?.display_name, username: profile?.username } }}
      onClose={() => setShareRow(null)} />
  )

  const Header = ({ badge }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <Feather size={14} color="var(--amber)" />
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--text)' }}>Your Take</span>
      {badge}
    </div>
  )

  // ── 0 takes → invitation ──
  if (reviews.length === 0) {
    return (
      <>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 14 }}>
          <Header />
          <button onClick={() => setEditTarget({ season: null, forceNew: false, milestone: null, isNew: true })}
            style={{ width: '100%', textAlign: 'left', background: 'transparent', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-dim)', fontSize: 13, padding: '12px 13px', cursor: 'pointer', fontFamily: 'var(--font)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={14} color="var(--amber)" />
            Write your full take on {show?.name}, rate it, say why.
          </button>
        </div>
        {composer}
      </>
    )
  }

  const first   = reviews[0]
  const latest  = reviews[reviews.length - 1]
  const multi   = reviews.length > 1
  const revisitTarget = { season: lastSeason, forceNew: true, isNew: true, milestone: showEnded ? 'finale' : 'season' }
  const canRevisit = lastSeason != null && (latest.season == null || latest.season < lastSeason || (showEnded && latest.milestone !== 'finale'))

  return (
    <>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 14 }}>
        <Header badge={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: latest.is_public ? 'var(--amber)' : 'var(--text-dim)', fontSize: 10, fontWeight: 700, marginLeft: 'auto' }}>
            {latest.is_public ? <><Globe size={10} /> Public</> : <><Lock size={10} /> Private</>}
          </span>
        } />

        {/* Your LATEST take */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>{takeLabel(latest)}</span>
          <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>· {fmtDate(latest.created_at, { month: 'short', year: 'numeric' })}</span>
          {latest.rating > 0 && <span style={{ marginLeft: 'auto' }}><ReelDisplay value={latest.rating} size={12} /></span>}
        </div>
        <div onClick={() => setEditTarget({ season: latest.season, forceNew: false, isNew: false, milestone: latest.milestone })} style={{ cursor: 'pointer' }}>
          <ReviewBody text={latest.body} contains_spoilers={latest.contains_spoilers}
            style={{ display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: 14, lineHeight: 1.6 }} />
        </div>

        {/* The arc, one line, links to the full timeline in the Reading Room */}
        {multi && (
          <button onClick={() => navigate(`/reviews?show=${show.id}&me=1`)} style={{
            width: '100%', marginTop: 12, display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(var(--amber-rgb),0.05)', border: '1px solid rgba(var(--amber-rgb),0.16)',
            borderRadius: 'var(--radius-sm)', padding: '9px 12px', cursor: 'pointer', fontFamily: 'var(--font)',
          }}>
            {first.rating > 0 && <ReelDisplay value={first.rating} size={11} />}
            <ArrowRight size={13} color="var(--text-dim)" />
            {latest.rating > 0 && <ReelDisplay value={latest.rating} size={11} />}
            <span style={{ marginLeft: 'auto', color: 'var(--amber)', fontSize: 12.5, fontWeight: 700 }}>
              See how your take changed →
            </span>
          </button>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <MiniBtn onClick={() => setEditTarget({ season: latest.season, forceNew: false, isNew: false, milestone: latest.milestone })}><Pencil size={11} /> Edit</MiniBtn>
          <MiniBtn onClick={() => setShareRow(latest)}><Share2 size={11} /> Share</MiniBtn>
          {canRevisit && (
            <button onClick={() => setEditTarget(revisitTarget)} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(var(--amber-rgb),0.1)', border: '1px solid rgba(var(--amber-rgb),0.28)', borderRadius: 'var(--radius-xs)', color: 'var(--amber)', fontSize: 12, fontWeight: 700, padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--font)' }}>
              <Plus size={12} /> {showEnded ? 'Final word' : 'Updated take'}
            </button>
          )}
        </div>
      </div>
      {composer}
      {shareSheet}
    </>
  )
}

function MiniBtn({ onClick, children }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick() }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, padding: '5px 11px', cursor: 'pointer', fontFamily: 'var(--font)' }}>
      {children}
    </button>
  )
}
